/**
 * Engine test creatures for the labs and look gate LG-1 until P1's generator lands: a tabby crusader cat (keyart
 * hero) and a snail knight, plus their props (cod-fish sword, lance, heater shield). Built with the art §5 canon
 * (H = 1, feet at y = 0, creature faces +Z, +X = its left). Deterministic.
 */
import type { CreatureMeshData, PropMeshData, Rig } from '../../shared';
import { MAT, REGION } from '../../content/palette';
import { MeshBuilder, blend, rigid, v3, type LoftRing, type V3 } from './builder';

const { add, mul } = v3;

const cos2 = (v: number): number => Math.cos(2 * Math.PI * v);
const sin2 = (v: number): number => Math.sin(2 * Math.PI * v);

/** Flat almond eye: sclera disc, iris, pupil, an upper lid shell with an ink crease (art §5.5 "solemn derp"). */
function eye(b: MeshBuilder, c: V3, side: number, o: { head: number; eye: number; lid: number; w: number; h: number; lid01: number; gaze: [number, number]; region: number }): void {
  const n: V3 = v3.norm([side * 0.28, 0.02, 1]);
  const ax: V3 = v3.norm(v3.cross([0, 1, 0], n));
  const ay: V3 = v3.cross(n, ax);
  const P = (u: number, v: number, w: number): V3 => add(add(add(c, mul(ax, u)), mul(ay, v)), mul(n, w));
  const N = 12;
  const almond = (t: number): [number, number] => {
    const a = t * Math.PI * 2;
    const x = Math.cos(a) * o.w;
    const y = Math.sin(a) * o.h * (1 - 0.25 * Math.cos(a) * side * 0.0);
    return [x, y];
  };
  // sclera
  for (let i = 0; i < N; i++) {
    const [x0, y0] = almond(i / N), [x1, y1] = almond((i + 1) / N);
    b.tri(P(0, 0, 0.002), P(x0, y0, 0), P(x1, y1, 0), { mat: MAT.sclera, region: o.region, skin: rigid(o.eye), outward: n, edge: 1, lw: 0.9 });
  }
  // iris + pupil (gaze offset)
  const gx = o.gaze[0] * o.w * 0.35, gy = o.gaze[1] * o.h * 0.35;
  const ri = o.h * 0.78;
  for (let i = 0; i < N; i++) {
    const a0 = (i / N) * Math.PI * 2, a1 = ((i + 1) / N) * Math.PI * 2;
    b.tri(P(gx, gy, 0.004), P(gx + Math.cos(a0) * ri, gy + Math.sin(a0) * ri, 0.003), P(gx + Math.cos(a1) * ri, gy + Math.sin(a1) * ri, 0.003),
      { mat: MAT.iris, region: o.region, skin: rigid(o.eye), outward: n, pat: [[0, 0], [1, 0], [1, 0]] });
    const rp = ri * 0.48;
    b.tri(P(gx, gy, 0.0055), P(gx + Math.cos(a0) * rp, gy + Math.sin(a0) * rp, 0.005), P(gx + Math.cos(a1) * rp, gy + Math.sin(a1) * rp, 0.005),
      { mat: MAT.pupil, region: o.region, skin: rigid(o.eye), outward: n });
  }
  // upper lid: covers the top lid01 of the eye, fur-coloured, ink crease on its lower edge
  const yl = o.h * (1 - 2 * o.lid01);
  const pts: Array<[number, number]> = [];
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    const x = -o.w * 1.12 + t * o.w * 2.24;
    pts.push([x, yl + Math.sin(t * Math.PI) * o.h * 0.12 - side * 0 * x]);
  }
  for (let i = 0; i < 8; i++) {
    const [x0, y0] = pts[i], [x1, y1] = pts[i + 1];
    b.tri(P(x0, y0, 0.0065), P(x1, y1, 0.0065), P((x0 + x1) / 2, o.h * 1.35, 0.004), { mat: MAT.fur, region: REGION.skull, skin: rigid(o.lid), outward: n, edge: 4, lw: 1.5 });
  }
}

/** Builds the tabby crusader (keyart hero). */
export function buildCat(rig: Rig): CreatureMeshData {
  const I = rig.index;
  const bi = (k: keyof typeof I): number => I[k] ?? 0;
  const P = bi('pelvis'), S1 = bi('spine_01'), S2 = bi('spine_02'), N = bi('neck'), H = bi('head');
  const b = new MeshBuilder();
  const UPX: V3 = [-1, 0, 0], FWD: V3 = [0, 0, 1];

  // torso (under the tabard; no hull — the tabard outlines it)
  b.loft({
    rings: [
      { c: [0, 0.3, -0.005], ru: 0.118, rf: 0.092, skin: rigid(P) },
      { c: [0, 0.4, 0.008], ru: 0.132, rf: 0.104, skin: blend(S1, P, 0.6) },
      { c: [0, 0.5, 0.005], ru: 0.122, rf: 0.094, skin: blend(S1, S2, 0.5) },
      { c: [0, 0.6, 0], ru: 0.1, rf: 0.08, skin: rigid(S2) },
      { c: [0, 0.665, 0.005], ru: 0.058, rf: 0.052, skin: blend(N, S2, 0.5) },
    ], sides: 8, up: UPX, front: FWD, mat: MAT.fur, region: REGION.torso, stagger: true, capStart: true, mirrorX: true, noHull: true,
  });
  // neck
  b.loft({
    rings: [
      { c: [0, 0.6, 0], ru: 0.06, rf: 0.055, skin: rigid(S2) },
      { c: [0, 0.66, 0.008], ru: 0.062, rf: 0.058, skin: rigid(N) },
      { c: [0, 0.72, 0.03], ru: 0.07, rf: 0.06, skin: blend(H, N, 0.6) },
    ], sides: 8, up: UPX, front: FWD, mat: MAT.fur, region: REGION.neck, mirrorX: true, noHull: true,
  });

  // tabard (lead-white dye 0) with the red cross (dye 1) painted per facet column, belt, skirt hem
  const tabRings: Array<[number, number, number, number]> = [
    [0.655, 0.07, 0.062, 0.012], [0.615, 0.122, 0.092, 0.006], [0.56, 0.135, 0.1, 0.008], [0.5, 0.142, 0.106, 0.01],
    [0.45, 0.146, 0.11, 0.012], [0.4, 0.15, 0.114, 0.012], [0.34, 0.163, 0.12, 0.01], [0.27, 0.18, 0.13, 0.008],
    [0.2, 0.198, 0.14, 0.004], [0.145, 0.212, 0.148, 0.0],
  ];
  const tabFlat = (v: number): number => 1 - 0.1 * Math.max(0, sin2(v)) ** 4;
  b.beginPart();
  b.loft({
    rings: tabRings.map(([y, ru, rf, z]) => ({ c: [0, y, z] as V3, ru, rf, skin: y > 0.5 ? rigid(S2) : y > 0.38 ? blend(S1, P, 0.5) : rigid(P), shape: tabFlat })),
    sides: 12, up: UPX, front: FWD, mat: MAT.dye0, region: REGION.torso, capStart: true, mirrorX: true, hemRings: [9], jitter: 0.02,
  });
  b.autoEdges(26, 0.6);
  b.autoEdges(40, 0.8);
  // belt + buckle
  b.loft({
    rings: [{ c: [0, 0.385, 0.012], ru: 0.158, rf: 0.121, skin: rigid(P) }, { c: [0, 0.415, 0.012], ru: 0.156, rf: 0.119, skin: rigid(P) }],
    sides: 12, up: UPX, front: FWD, mat: MAT.leather, region: REGION.torso, mirrorX: true, hemRings: [0, 1], hullWeight: 0.5, jitter: 0,
  });
  b.loft({
    rings: [{ c: [0, 0.4, 0.125], ru: 0.022, rf: 0.008, skin: rigid(P) }, { c: [0, 0.4, 0.135], ru: 0.022, rf: 0.008, skin: rigid(P) }],
    sides: 4, phase: 0.5, up: [0, 1, 0], front: [1, 0, 0], mat: MAT.gold, region: REGION.torso, capStart: true, capEnd: true, noHull: true, jitter: 0,
  });

  // the red cross of the tabard (dye 1), laid on the flattened front and back as thin lofts following the cloth
  const frontAt = (y: number, x: number, back: boolean): V3 => {
    let i = 0;
    while (i < tabRings.length - 2 && tabRings[i + 1][0] > y) i++;
    const [y0, ru0, rf0, z0] = tabRings[i], [y1, ru1, rf1, z1] = tabRings[i + 1];
    const t = Math.min(1, Math.max(0, (y0 - y) / (y0 - y1)));
    const ru = ru0 + (ru1 - ru0) * t, rf = (rf0 + (rf1 - rf0) * t) * 0.9, zc = z0 + (z1 - z0) * t;
    const k = Math.sqrt(Math.max(0, 1 - (x / ru) ** 2));
    return [x, y, back ? zc - rf * k - 0.006 : zc + rf * k + 0.006];
  };
  for (const back of [false, true]) {
    const bars: Array<{ from: [number, number]; to: [number, number]; w: number }> = [
      { from: [0, 0.6], to: [0, 0.42], w: 0.026 }, { from: [-0.085, 0.535], to: [0.085, 0.535], w: 0.022 },
      { from: [0, 0.34], to: [0, 0.16], w: 0.026 }, { from: [-0.1, 0.275], to: [0.1, 0.275], w: 0.022 },
    ];
    for (const bar of bars) {
      const rings: LoftRing[] = [];
      for (let i = 0; i <= 4; i++) {
        const t = i / 4;
        const x = bar.from[0] + (bar.to[0] - bar.from[0]) * t, y = bar.from[1] + (bar.to[1] - bar.from[1]) * t;
        const c = frontAt(y, x, back);
        rings.push({ c, ru: bar.w, rf: 0.004, skin: y > 0.5 ? rigid(S2) : y > 0.38 ? blend(S1, P, 0.5) : rigid(P) });
      }
      const vertical = bar.from[0] === bar.to[0];
      b.beginPart();
      b.loft({ rings, sides: 4, phase: 0.5, up: vertical ? [1, 0, 0] : [0, 1, 0], front: [0, 0, back ? -1 : 1], mat: MAT.dye1, region: REGION.torso, capStart: true, capEnd: true, jitter: 0, noHull: true });
      b.autoEdges(60, 0.8);
    }
  }

  // cape (vermilion dye 1), hanging behind the shoulders, wrapping forward at the sides
  b.beginPart();
  b.loft({
    rings: [
      { c: [0, 0.645, -0.05], ru: 0.11, rf: 0.016, skin: rigid(S2), bump: (v) => [0, 0, 0.06 * cos2(v) ** 2] },
      { c: [0, 0.55, -0.125], ru: 0.16, rf: 0.018, skin: blend(S2, P, 0.7), bump: (v) => [0, 0, 0.08 * cos2(v) ** 2] },
      { c: [0, 0.42, -0.165], ru: 0.19, rf: 0.02, skin: blend(P, S2, 0.6), bump: (v) => [0, 0, 0.08 * cos2(v) ** 2] },
      { c: [0, 0.26, -0.195], ru: 0.215, rf: 0.02, skin: rigid(P), bump: (v) => [0, 0, 0.07 * cos2(v) ** 2] },
      { c: [0, 0.12, -0.22], ru: 0.23, rf: 0.02, skin: rigid(P), bump: (v) => [0, 0, 0.06 * cos2(v) ** 2] },
    ], sides: 10, up: UPX, front: FWD, mat: MAT.dye1, region: REGION.extras, capStart: true, capEnd: true, mirrorX: true, hemRings: [4], jitter: 0.04,
  });
  b.autoEdges(55, 0.7);

  // head: staggered loft, flat human-like face, slight muzzle (art §5.4–§5.5)
  const faceFlat = (v: number): number => 1 - 0.16 * Math.max(0, sin2(v)) ** 3;
  const headRings: LoftRing[] = [
    { c: [0, 0.69, 0.045], ru: 0.05, rf: 0.045, skin: rigid(H) },
    { c: [0, 0.725, 0.04], ru: 0.1, rf: 0.088, skin: rigid(H), shape: faceFlat },
    { c: [0, 0.775, 0.035], ru: 0.128, rf: 0.108, skin: rigid(H), shape: faceFlat, bump: (v) => [0, 0, 0.022 * Math.max(0, sin2(v)) ** 6] },
    { c: [0, 0.835, 0.03], ru: 0.14, rf: 0.115, skin: rigid(H), shape: faceFlat },
    { c: [0, 0.895, 0.025], ru: 0.136, rf: 0.112, skin: rigid(H), shape: faceFlat },
    { c: [0, 0.95, 0.018], ru: 0.11, rf: 0.095, skin: rigid(H) },
    { c: [0, 0.99, 0.01], ru: 0.06, rf: 0.055, skin: rigid(H) },
  ];
  b.beginPart();
  b.loft({ rings: headRings, sides: 10, up: UPX, front: FWD, mat: MAT.fur, region: REGION.skull, stagger: true, capStart: true, capEnd: true, mirrorX: true, jitter: 0.03, seed: 7 });
  b.autoEdges(38, 0.9);
  // muzzle pad (belly-coloured via region 1) and nose leather
  b.loft({
    rings: [
      { c: [0, 0.765, 0.13], ru: 0.062, rf: 0.02, skin: rigid(H) },
      { c: [0, 0.79, 0.142], ru: 0.06, rf: 0.022, skin: rigid(H) },
      { c: [0, 0.815, 0.138], ru: 0.04, rf: 0.016, skin: rigid(H) },
    ], sides: 8, up: [0, 0, 1], front: [0, -1, 0], mat: MAT.fur, region: REGION.muzzle, capStart: true, capEnd: true, mirrorX: true, noHull: true, jitter: 0.02,
  });
  const nz = 0.158;
  b.tri([-0.022, 0.815, nz], [0.022, 0.815, nz], [0, 0.792, nz + 0.006], { mat: MAT.flesh, region: REGION.muzzle, skin: rigid(H), outward: [0, 0.2, 1], edge: 7, lw: 0.8 });
  // philtrum + mouth: ink strokes, corners down 4° (solemn)
  const mz = 0.152;
  const stroke = (a: V3, c: V3, w: number): void => {
    const d = v3.norm(v3.sub(c, a));
    const nrm: V3 = [-d[1] * w, d[0] * w, 0];
    b.tri(add(a, nrm), v3.sub(a, nrm), add(c, nrm), { mat: MAT.pupil, region: REGION.muzzle, skin: rigid(H), outward: [0, 0, 1] });
    b.tri(v3.sub(a, nrm), v3.sub(c, nrm), add(c, nrm), { mat: MAT.pupil, region: REGION.muzzle, skin: rigid(H), outward: [0, 0, 1] });
  };
  stroke([0, 0.792, mz + 0.004], [0, 0.772, mz + 0.002], 0.0028);
  stroke([0, 0.772, mz + 0.002], [0.036, 0.762, mz - 0.006], 0.0028);
  stroke([0, 0.772, mz + 0.002], [-0.036, 0.762, mz - 0.006], 0.0028);
  // whiskers (lead white, thin)
  for (const s of [1, -1]) for (const [dy, len] of [[0.006, 0.12], [-0.006, 0.13], [-0.016, 0.11]]) {
    const a: V3 = [s * 0.05, 0.785 + dy, 0.152];
    const c: V3 = [s * (0.05 + len), 0.785 + dy * 2.2 - 0.01, 0.12];
    const w = 0.0022;
    b.tri([a[0], a[1] + w, a[2]], [a[0], a[1] - w, a[2]], [c[0], c[1], c[2]], { mat: MAT.sclera, region: REGION.muzzle, skin: rigid(H), outward: [0, 0, 1] });
  }
  // eyes: almond, sclera visible, heavy upper lids (Летописец: 45 % cover), gaze slightly down
  for (const s of [1, -1]) {
    eye(b, [s * 0.052, 0.842, 0.142], s, { head: H, eye: bi(s > 0 ? 'eye_L' : 'eye_R'), lid: bi(s > 0 ? 'lid_L' : 'lid_R'), w: 0.028, h: 0.019, lid01: 0.42, gaze: [-0.15 * s, -0.25], region: REGION.skull });
    // brow: dark 2-facet ridge, inner end slightly lower (solemn / grumpy)
    const bx0 = s * 0.022, bx1 = s * 0.088;
    b.tri([bx0, 0.872, 0.152], [bx1, 0.888, 0.135], [bx1, 0.878, 0.137], { mat: MAT.pupil, region: REGION.skull, skin: rigid(bi(s > 0 ? 'brow_L' : 'brow_R')), outward: [0, 0, 1] });
    b.tri([bx0, 0.872, 0.152], [bx0, 0.864, 0.153], [bx1, 0.878, 0.137], { mat: MAT.pupil, region: REGION.skull, skin: rigid(bi(s > 0 ? 'brow_L' : 'brow_R')), outward: [0, 0, 1] });
  }
  // ears: triangular lofts, inner flesh on the front face
  for (const s of [1, -1]) {
    const e1 = bi(s > 0 ? 'ear_L_01' : 'ear_R_01'), e2 = bi(s > 0 ? 'ear_L_02' : 'ear_R_02');
    b.beginPart();
    b.loft({
      rings: [
        { c: [s * 0.098, 0.925, 0.0], ru: 0.05, rf: 0.026, skin: rigid(H) },
        { c: [s * 0.112, 0.99, -0.008], ru: 0.034, rf: 0.016, skin: rigid(e1) },
        { c: [s * 0.122, 1.06, -0.014], ru: 0.006, rf: 0.004, skin: rigid(e2) },
      ], sides: 3, up: [s, 0, 0], front: FWD, mat: MAT.fur, region: REGION.ears, capEnd: true, jitter: 0, phase: 0.25,
      matAt: (_i, j) => (j === 0 ? MAT.flesh : -1),
    });
    b.autoEdges(35, 0.8);
  }

  // arms: fur, banded by the mackerel pattern; paws
  for (const s of [1, -1]) {
    const ua = bi(s > 0 ? 'upperarm_L' : 'upperarm_R'), fa = bi(s > 0 ? 'forearm_L' : 'forearm_R'), ha = bi(s > 0 ? 'hand_L' : 'hand_R');
    b.loft({
      rings: [
        { c: [s * 0.112, 0.625, 0], ru: 0.05, rf: 0.052, skin: blend(ua, S2, 0.6) },
        { c: [s * 0.122, 0.54, 0], ru: 0.046, rf: 0.047, skin: rigid(ua) },
        { c: [s * 0.125, 0.45, 0.002], ru: 0.04, rf: 0.042, skin: blend(ua, fa, 0.5) },
        { c: [s * 0.128, 0.36, 0.006], ru: 0.036, rf: 0.037, skin: rigid(fa) },
        { c: [s * 0.13, 0.28, 0.011], ru: 0.033, rf: 0.033, skin: blend(fa, ha, 0.5) },
        { c: [s * 0.131, 0.235, 0.02], ru: 0.036, rf: 0.032, skin: rigid(ha) },
        { c: [s * 0.131, 0.195, 0.024], ru: 0.026, rf: 0.024, skin: rigid(ha) },
      ], sides: 6, up: FWD, front: [s, 0, 0], mat: MAT.fur, region: REGION.forearms, capStart: true, capEnd: true, jitter: 0.03, seed: 11 + s,
    });
  }
  // legs: thighs, calves, long cat feet
  for (const s of [1, -1]) {
    const th = bi(s > 0 ? 'thigh_L' : 'thigh_R'), ca = bi(s > 0 ? 'calf_L' : 'calf_R'), fo = bi(s > 0 ? 'foot_L' : 'foot_R'), to = bi(s > 0 ? 'toe_L' : 'toe_R');
    b.loft({
      rings: [
        { c: [s * 0.068, 0.33, 0], ru: 0.06, rf: 0.065, skin: blend(th, P, 0.7) },
        { c: [s * 0.068, 0.25, 0.008], ru: 0.052, rf: 0.056, skin: rigid(th) },
        { c: [s * 0.068, 0.18, 0.012], ru: 0.044, rf: 0.048, skin: blend(th, ca, 0.5) },
        { c: [s * 0.069, 0.11, 0.004], ru: 0.038, rf: 0.04, skin: rigid(ca) },
        { c: [s * 0.07, 0.055, -0.008], ru: 0.036, rf: 0.036, skin: blend(ca, fo, 0.5) },
      ], sides: 6, up: FWD, front: [s, 0, 0], mat: MAT.fur, region: REGION.shins, capStart: true, jitter: 0.03, seed: 21 + s,
    });
    b.beginPart();
    b.loft({
      rings: [
        { c: [s * 0.07, 0.04, -0.035], ru: 0.03, rf: 0.032, skin: rigid(fo) },
        { c: [s * 0.07, 0.032, 0.02], ru: 0.034, rf: 0.04, skin: rigid(fo) },
        { c: [s * 0.07, 0.024, 0.075], ru: 0.028, rf: 0.042, skin: blend(to, fo, 0.5) },
        { c: [s * 0.07, 0.018, 0.105], ru: 0.016, rf: 0.032, skin: rigid(to) },
      ], sides: 6, up: [0, 1, 0], front: [s, 0, 0], mat: MAT.fur, region: REGION.shins, capStart: true, capEnd: true, jitter: 0.02,
    });
    b.autoEdges(40, 0.7);
  }

  // tail: S-curve «question mark», never straight (art §5.4)
  const t1 = bi('tail_1'), t2 = bi('tail_2'), t3 = bi('tail_3'), t4 = bi('tail_4');
  b.loft({
    rings: [
      { c: [0, 0.34, -0.09], ru: 0.034, rf: 0.034, skin: blend(t1, P, 0.7) },
      { c: [0, 0.3, -0.17], ru: 0.033, rf: 0.033, skin: rigid(t1) },
      { c: [0, 0.31, -0.25], ru: 0.032, rf: 0.032, skin: blend(t2, t1, 0.6) },
      { c: [0, 0.38, -0.31], ru: 0.031, rf: 0.031, skin: rigid(t2) },
      { c: [0, 0.5, -0.33], ru: 0.03, rf: 0.03, skin: blend(t3, t2, 0.6) },
      { c: [0, 0.62, -0.3], ru: 0.029, rf: 0.029, skin: rigid(t3) },
      { c: [0, 0.71, -0.24], ru: 0.028, rf: 0.028, skin: blend(t4, t3, 0.6) },
      { c: [0, 0.76, -0.16], ru: 0.026, rf: 0.026, skin: rigid(t4) },
      { c: [0, 0.75, -0.105], ru: 0.02, rf: 0.02, skin: rigid(t4) },
    ], sides: 6, up: [1, 0, 0], front: [0, 1, 0], mat: MAT.fur, region: REGION.tail, capEnd: true, jitter: 0.02,
  });
  return b.build(0);
}

/** Snail knight: slug body, spiral shell, eye stalks, great helm. */
export function buildSnail(rig: Rig): CreatureMeshData {
  const I = rig.index;
  const bi = (k: keyof typeof I): number => I[k] ?? 0;
  const P = bi('pelvis'), S1 = bi('spine_01'), S2 = bi('spine_02'), N = bi('neck'), H = bi('head'), SH = bi('shell');
  const T1 = bi('tail_1'), T2 = bi('tail_2');
  const b = new MeshBuilder();
  // body: foot along the ground rising into the neck and head
  const flatBottom = (v: number): number => (cos2(v) < -0.3 ? 0.75 : 1);
  b.beginPart();
  b.loft({
    rings: [
      { c: [0, 0.022, -0.44], ru: 0.018, rf: 0.026, skin: rigid(T2) },
      { c: [0, 0.04, -0.34], ru: 0.05, rf: 0.065, skin: blend(T2, T1, 0.5) },
      { c: [0, 0.055, -0.2], ru: 0.075, rf: 0.095, skin: rigid(T1), shape: flatBottom },
      { c: [0, 0.065, -0.04], ru: 0.085, rf: 0.11, skin: blend(P, T1, 0.7), shape: flatBottom },
      { c: [0, 0.075, 0.1], ru: 0.088, rf: 0.112, skin: blend(S1, P, 0.6), shape: flatBottom },
      { c: [0, 0.13, 0.22], ru: 0.086, rf: 0.105, skin: blend(S2, S1, 0.6) },
      { c: [0, 0.24, 0.3], ru: 0.08, rf: 0.092, skin: rigid(S2) },
      { c: [0, 0.36, 0.33], ru: 0.075, rf: 0.085, skin: blend(N, S2, 0.6) },
      { c: [0, 0.46, 0.34], ru: 0.072, rf: 0.08, skin: rigid(H) },
      { c: [0, 0.53, 0.34], ru: 0.05, rf: 0.06, skin: rigid(H) },
    ], sides: 9, up: [0, 1, 0], front: [1, 0, 0], mat: MAT.fur, region: REGION.torso, capStart: true, capEnd: true, mirrorX: true, jitter: 0.04, seed: 5,
  });
  b.autoEdges(45, 0.6);
  // great helm (iron) over the head, visor slit as an ink band
  b.beginPart();
  b.loft({
    rings: [
      { c: [0, 0.4, 0.34], ru: 0.088, rf: 0.094, skin: rigid(H) },
      { c: [0, 0.47, 0.342], ru: 0.092, rf: 0.098, skin: rigid(H) },
      { c: [0, 0.5, 0.342], ru: 0.092, rf: 0.098, skin: rigid(H) },
      { c: [0, 0.54, 0.342], ru: 0.09, rf: 0.096, skin: rigid(H) },
      { c: [0, 0.585, 0.34], ru: 0.075, rf: 0.08, skin: rigid(H) },
      { c: [0, 0.605, 0.34], ru: 0.04, rf: 0.045, skin: rigid(H) },
    ], sides: 10, up: [0, 0, 1], front: [1, 0, 0], mat: MAT.iron, region: REGION.skull, capEnd: true, mirrorX: true, hemRings: [0, 3], jitter: 0.015,
    matAt: (i, j) => (i === 1 && (j <= 2 || j >= 8) ? MAT.pupil : -1),
  });
  b.autoEdges(35, 0.8);
  // eye stalks + eye balls (pupils look forward and towards the camera side, local −X)
  for (const s of [1, -1]) {
    const e1 = bi(s > 0 ? 'ear_L_01' : 'ear_R_01'), e2 = bi(s > 0 ? 'ear_L_02' : 'ear_R_02');
    const tip: V3 = [s * 0.07, 0.8, 0.4];
    b.loft({
      rings: [
        { c: [s * 0.032, 0.57, 0.335], ru: 0.017, rf: 0.017, skin: rigid(H) },
        { c: [s * 0.048, 0.66, 0.36], ru: 0.014, rf: 0.014, skin: rigid(e1) },
        { c: [s * 0.062, 0.75, 0.385], ru: 0.012, rf: 0.012, skin: rigid(e2) },
        { c: [tip[0], tip[1] - 0.025, tip[2]], ru: 0.011, rf: 0.011, skin: rigid(e2) },
      ], sides: 5, up: [0, 0, 1], front: [1, 0, 0], mat: MAT.fur, region: REGION.ears, jitter: 0,
    });
    b.loft({
      rings: [
        { c: [tip[0], tip[1] - 0.032, tip[2]], ru: 0.012, rf: 0.012, skin: rigid(e2) },
        { c: [tip[0], tip[1] - 0.012, tip[2]], ru: 0.032, rf: 0.032, skin: rigid(e2) },
        { c: [tip[0], tip[1] + 0.014, tip[2]], ru: 0.03, rf: 0.03, skin: rigid(e2) },
        { c: [tip[0], tip[1] + 0.034, tip[2]], ru: 0.008, rf: 0.008, skin: rigid(e2) },
      ], sides: 8, up: [0, 0, 1], front: [1, 0, 0], mat: MAT.sclera, region: REGION.ears, capStart: true, capEnd: true, jitter: 0, hullWeight: 0.8,
    });
    const nrm = v3.norm([-0.55, 0.05, 0.84]);
    const pc: V3 = add([tip[0], tip[1], tip[2]], mul(nrm, 0.033));
    const ax = v3.norm(v3.cross([0, 1, 0], nrm)), ay = v3.cross(nrm, ax);
    for (let i = 0; i < 10; i++) {
      const a0 = (i / 10) * Math.PI * 2, a1 = ((i + 1) / 10) * Math.PI * 2, r = 0.014;
      const P = (a: number, rr: number): V3 => add(add(pc, mul(ax, Math.cos(a) * rr)), mul(ay, Math.sin(a) * rr));
      b.tri(add(pc, mul(nrm, 0.002)), P(a0, r), P(a1, r), { mat: MAT.pupil, region: REGION.ears, skin: rigid(e2), outward: nrm });
    }
  }
  // shell: logarithmic spiral tube in the creature's YZ plane (its side faces the camera in profile staging)
  b.beginPart();
  const centre: V3 = [0, 0.3, -0.1];
  const k = 0.22;
  const rings: LoftRing[] = [];
  const turns = 2.6;
  const steps = 30;
  for (let i = 0; i <= steps; i++) {
    const th = (i / steps) * turns * Math.PI * 2;
    const R = 0.2 * Math.exp(-k * th);
    const a = th + Math.PI * 0.62;
    const c: V3 = add(centre, [0, Math.sin(a) * R, Math.cos(a) * R]);
    const tr = Math.max(0.006, R * 0.47);
    rings.push({ c, ru: tr * 1.25, rf: tr, skin: rigid(SH) });
  }
  b.loft({ rings, sides: 9, up: [1, 0, 0], front: [0, 1, 0], mat: MAT.shell, region: REGION.extras, capStart: true, capEnd: true, mirrorX: true, jitter: 0.03, seed: 9 });
  b.autoEdges(50, 0.9);
  return b.build(0);
}

// ── props (art §3.2 render layout, boneA = boneB = 0; grip at the origin, along +Y) ─────────────────────────────────

function asProp(b: MeshBuilder): PropMeshData {
  const m = b.build(0);
  return { vertices: m.vertices, vertexCount: m.vertexCount, hull: m.hull, grip: [0, 0, 0] };
}

/** The cod-fish sword «Треска» (keyart): leather grip, gilt crossguard and pommel, a silver fish for a blade. */
export function buildFishSword(): PropMeshData {
  const b = new MeshBuilder();
  const s = rigid(0);
  b.loft({ rings: [{ c: [0, -0.07, 0], ru: 0.014, rf: 0.014, skin: s }, { c: [0, 0.04, 0], ru: 0.013, rf: 0.013, skin: s }], sides: 6, up: [0, 0, 1], front: [1, 0, 0], mat: MAT.leather, region: REGION.extras, capStart: true, capEnd: true, jitter: 0 });
  b.loft({ rings: [{ c: [0, -0.1, 0], ru: 0.012, rf: 0.012, skin: s }, { c: [0, -0.085, 0], ru: 0.022, rf: 0.022, skin: s }, { c: [0, -0.068, 0], ru: 0.012, rf: 0.012, skin: s }], sides: 6, up: [0, 0, 1], front: [1, 0, 0], mat: MAT.gold, region: REGION.extras, capStart: true, capEnd: true, jitter: 0 });
  // crossguard: a square bar along Z (the blade's width axis)
  b.loft({ rings: [{ c: [0, 0.048, -0.075], ru: 0.014, rf: 0.014, skin: s }, { c: [0, 0.048, 0.075], ru: 0.014, rf: 0.014, skin: s }], sides: 4, phase: 0.5, up: [0, 1, 0], front: [1, 0, 0], mat: MAT.gold, region: REGION.extras, capStart: true, capEnd: true, jitter: 0 });
  // fish blade: flat side faces ±X, width along Z; head at the top
  b.beginPart();
  const fish: Array<[number, number, number]> = [
    [0.06, 0.03, 0.009], [0.11, 0.05, 0.013], [0.2, 0.062, 0.017], [0.31, 0.066, 0.019], [0.42, 0.062, 0.019], [0.5, 0.055, 0.018], [0.56, 0.04, 0.015], [0.6, 0.016, 0.009],
  ];
  b.loft({
    rings: fish.map(([y, w, t]) => ({ c: [0, y, 0.008] as V3, ru: w, rf: t, skin: s })),
    sides: 8, up: [0, 0, 1], front: [1, 0, 0], mat: MAT.iron, region: REGION.extras, capStart: true, capEnd: true, hemRings: [5], jitter: 0.02,
  });
  b.autoEdges(45, 0.8);
  // tail fin at the guard end
  b.prism([[-0.05, 0], [0.05, 0], [0.0, 0.05]], { origin: [0, 0.035, 0.008], ax: [0, 0, 1], ay: [0, 1, 0], az: [1, 0, 0], thick: 0.008, matFront: MAT.iron, region: REGION.extras, skin: s });
  // fish eyes on both flat sides
  for (const side of [1, -1]) {
    const c: V3 = [side * 0.02, 0.535, 0.022];
    for (let i = 0; i < 8; i++) {
      const a0 = (i / 8) * Math.PI * 2, a1 = ((i + 1) / 8) * Math.PI * 2;
      const P = (r: number, a: number, x: number): V3 => [c[0] + side * x, c[1] + Math.sin(a) * r, c[2] + Math.cos(a) * r];
      b.tri(P(0, 0, 0.001), P(0.013, a0, 0), P(0.013, a1, 0), { mat: MAT.sclera, region: REGION.extras, skin: s, outward: [side, 0, 0], edge: 1, lw: 0.8 });
      b.tri(P(0, 0, 0.003), P(0.006, a0, 0.002), P(0.006, a1, 0.002), { mat: MAT.pupil, region: REGION.extras, skin: s, outward: [side, 0, 0] });
    }
  }
  return asProp(b);
}

/** A jousting lance, striped vermilion and lead white, with an iron vamplate. */
export function buildLance(): PropMeshData {
  const b = new MeshBuilder();
  const s = rigid(0);
  const n = 12;
  const rings: LoftRing[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    rings.push({ c: [0, -0.3 + t * 1.45, 0], ru: 0.028 * (1 - t) + 0.004, rf: 0.028 * (1 - t) + 0.004, skin: s });
  }
  b.loft({ rings, sides: 6, up: [0, 0, 1], front: [1, 0, 0], mat: MAT.dye1, region: REGION.extras, capStart: true, capEnd: true, jitter: 0, matAt: (i) => (i % 2 === 0 ? MAT.dye1 : MAT.parchment), hemRings: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] });
  b.loft({ rings: [{ c: [0, 0.02, 0], ru: 0.03, rf: 0.03, skin: s }, { c: [0, 0.1, 0], ru: 0.075, rf: 0.075, skin: s }, { c: [0, 0.11, 0], ru: 0.06, rf: 0.06, skin: s }], sides: 8, up: [0, 0, 1], front: [1, 0, 0], mat: MAT.iron, region: REGION.extras, capEnd: true, jitter: 0 });
  // pennant near the tip
  b.prism([[0, 0], [0.16, 0.03], [0.12, 0.07], [0.17, 0.11], [0, 0.11]], { origin: [0, 0.82, 0], ax: [0, 0, -1], ay: [0, 1, 0], az: [1, 0, 0], thick: 0.006, matFront: MAT.dye1, matSide: MAT.dye1, region: REGION.extras, skin: s, frontEdges: false });
  return asProp(b);
}

/** Heater shield: azure field, gilt rim and a gilt fleur-de-lis (rule of tincture: metal on colour). Front = +Z. */
export function buildShield(): PropMeshData {
  const b = new MeshBuilder();
  const s = rigid(0);
  const outline: Array<[number, number]> = [];
  const W = 0.15, Hh = 0.2;
  outline.push([-W, Hh], [-W, Hh * 0.15]);
  for (let i = 1; i <= 5; i++) { const t = i / 6; outline.push([-W * (1 - t) ** 0.8, Hh * 0.15 - t * (Hh * 1.15)]); }
  outline.push([0, -Hh]);
  for (let i = 5; i >= 1; i--) { const t = i / 6; outline.push([W * (1 - t) ** 0.8, Hh * 0.15 - t * (Hh * 1.15)]); }
  outline.push([W, Hh * 0.15], [W, Hh]);
  b.prism(outline.slice().reverse(), { origin: [0, 0, 0], ax: [1, 0, 0], ay: [0, 1, 0], az: [0, 0, 1], thick: 0.03, matFront: MAT.gold, matBack: MAT.wood, matSide: MAT.gold, region: REGION.extras, skin: s });
  const inner = outline.map(([x, y]) => [x * 0.84, y * 0.86 + 0.012] as [number, number]);
  b.prism(inner.slice().reverse(), { origin: [0, 0, 0.012], ax: [1, 0, 0], ay: [0, 1, 0], az: [0, 0, 1], thick: 0.01, matFront: MAT.dye2, region: REGION.extras, skin: s, noHull: true });
  // fleur-de-lis: central petal, two curled side petals, a band
  const fz = 0.02;
  const lily = (pts: Array<[number, number]>): void => b.prism(pts.slice().reverse(), { origin: [0, 0, fz], ax: [1, 0, 0], ay: [0, 1, 0], az: [0, 0, 1], thick: 0.006, matFront: MAT.gold, region: REGION.extras, skin: s, noHull: true, frontEdges: true, lw: 0.7 });
  lily([[0, 0.13], [0.03, 0.06], [0.02, -0.01], [0, -0.03], [-0.02, -0.01], [-0.03, 0.06]]);
  lily([[0.025, 0.0], [0.06, 0.06], [0.1, 0.06], [0.11, 0.02], [0.08, -0.03], [0.04, -0.02]]);
  lily([[-0.025, 0.0], [-0.04, -0.02], [-0.08, -0.03], [-0.11, 0.02], [-0.1, 0.06], [-0.06, 0.06]]);
  lily([[-0.05, -0.025], [0.05, -0.025], [0.05, -0.045], [-0.05, -0.045]]);
  lily([[0, -0.045], [0.025, -0.08], [0, -0.1], [-0.025, -0.08]]);
  return asProp(b);
}
